import { ChildDto, Config } from '@ecdlink/core';
import {
  AddChildCaregiverTokenModelInput,
  AddChildLearnerTokenModelInput,
  AddChildRegistrationTokenModelInput,
  AddChildSiteAddressTokenModelInput,
  AddChildTokenModelInput,
  AddChildUserConsentTokenModelInput,
  ChildInput,
} from '@ecdlink/graphql';
import { ChildRegistrationDetails } from '../../pages/child/caregiver-child-registration/caregiver-child-registration.types';
import { api } from '../axios.helper';

class ChildService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  async getChildren(): Promise<ChildDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllChild',
    });

    if (response.status !== 200) {
      throw new Error('Get Children Failed - Server connection error');
    }

    return response.data.data.GetAllChild;
  }

  async getChildrenForCoach(userId: string): Promise<ChildDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      variables: {
        userId,
      },
      id: 'AllChildrenForCoach',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Children For Coach Failed - Server connection error'
      );
    }

    return response.data.data.allChildrenForCoach;
  }

  async getChildrenForCoachV2(): Promise<ChildDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      id: `allChildrenForCoachV2`,
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Children For Coach Failed - Server connection error'
      );
    }

    return response.data.data.allChildrenForCoachV2;
  }

  async getChildrenForPractitioner(userId: string): Promise<ChildDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      variables: {
        userId,
      },
      id: 'AllChildrenForPractitioner',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Children For Practitioner Failed - Server connection error'
      );
    }

    return response.data.data.allChildrenForPractitioner;
  }

  async updateChild(id: string, input: ChildInput): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'UpdateChild',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating child failed - Server connection error');
    }

    return true;
  }

  async updateLearnerHierarchy(
    practitionerId: string,
    learnerId: string
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `UpdateLearnerHierarchy`,
      variables: {
        practitionerId: practitionerId,
        learnerId: learnerId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Updating learner hierarchy failed - Server connection error'
      );
    }

    return true;
  }

  async calculateChildrenRegistrationRemoval(userId: string): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'CalculateChildrenRegistrationRemoval',
      variables: {
        userId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Calculate Children Registration Removal failed - Server connection error'
      );
    }

    return true;
  }

  async createChild(input: ChildInput): Promise<ChildDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `createChild`,
      variables: {
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating child failed - Server connection error');
    }

    return response.data.data.createChild;
  }

  async generateCaregiverChildToken(
    firstname: string,
    surname: string,
    classgroupId: string
  ): Promise<string> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'generateCaregiverChildToken',
      variables: {
        firstname: firstname,
        surname: surname,
        classgroupId: classgroupId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'adding caregiver child registration token - Server connection error'
      );
    }

    return response.data.data.generateCaregiverChildToken;
  }

  async refreshCaregiverChildToken(
    childId: string,
    classgroupId: string
  ): Promise<string> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `refreshCaregiverChildToken`,
      variables: {
        childId: childId,
        classgroupId: classgroupId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'refreshing caregiver child registration token - Server connection error'
      );
    }

    return response.data.data.refreshCaregiverChildToken;
  }

  async openAccessAddChildDetail(
    token: string
  ): Promise<ChildRegistrationDetails> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      variables: {
        token: token,
      },
      id: 'OpenAccessAddChildDetail',
    });

    if (response.status !== 200) {
      throw new Error(
        'refreshing caregiver child registration token - Server connection error'
      );
    }

    return response.data.data.openAccessAddChildDetail;
  }

  async openAccessAddChild(
    token: string,
    caregiver: AddChildCaregiverTokenModelInput,
    learner: AddChildLearnerTokenModelInput,
    siteAddress: AddChildSiteAddressTokenModelInput,
    child: AddChildTokenModelInput,
    registration?: AddChildRegistrationTokenModelInput,
    consent?: AddChildUserConsentTokenModelInput
  ): Promise<string> {
    const apiInstance = await api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: `openAccessAddChild`,
      variables: {
        token: token,
        caregiver: caregiver,
        learner: learner,
        siteAddress: siteAddress,
        child: child,
        registration: registration,
        consent: consent,
      },
    });

    if (response.status !== 200) {
      throw new Error('adding token child failed - Server connection error');
    }
    return response.data.data.openAccessAddChild;
  }

  async childCreatedByDetail(
    practitionerId: string,
    firstName: string,
    surname: string
  ): Promise<ChildDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      variables: {
        practitionerId,
        firstName,
        surname,
      },
      id: 'GetChildCreatedByDetail',
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Children For Practitioner Failed - Server connection error'
      );
    }

    return response.data.data.childCreatedByDetail;
  }

  async checkDuplicateChild(
    firstName: string,
    surname: string,
    childDob: string | undefined,
    childIdNo: string,
    caregiverIdNo: string,
    genderId: string,
    childId: string | undefined
  ): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      variables: {
        firstName,
        surname,
        childDob,
        childIdNo,
        caregiverIdNo,
        genderId,
        childId,
      },
      id: 'DuplicateChild',
    });

    if (response.status !== 200) {
      throw new Error(
        `Duplicate check failed - Server connection error: ${response.status}`
      );
    }

    console.log('Duplicate check response:', response);

    return response.data.data.duplicateChild;
  }

  async checkDuplicateChildForOfflineSync(
    firstName: string | undefined,
    surname: string | undefined,
    childDob: string | undefined,
    childIdNo: string | undefined,
    caregiverIdNo: string | undefined,
    genderId: string | undefined,
    classroomGroupId: string | undefined
  ): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<any>(``, {
      variables: {
        firstName,
        surname,
        childDob,
        childIdNo,
        caregiverIdNo,
        genderId,
        classroomGroupId,
      },
      id: 'GetDuplicateChildForOfflineSync',
    });

    if (response.status !== 200) {
      throw new Error(
        `Duplicate check failed - Server connection error: ${response.status}`
      );
    }

    console.log('Duplicate check response:', response);

    return response.data.data.duplicateChildForOfflineSync;
  }

  async getChildByChildId(childId: string): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    try {
      const response = await apiInstance.post<any>(``, {
        variables: {
          childId,
        },
        id: 'GetChildByChildId',
      });

      if (response.status !== 200) {
        throw new Error(
          `Failed to fetch child - Server connection error: ${response.status}`
        );
      }

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      return response.data.data.childByChildId;
    } catch (error) {
      console.error('Error fetching child:', error);
      throw error;
    }
  }
}

export default ChildService;
